import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  ArrowLeft, User, Store, Truck, Package, MapPin, CreditCard,
  Clock, Eye, ShieldCheck, Zap, Loader2, Copy, Globe, Smartphone,
  CheckCircle, Search, Upload, Download, FileText, Timer, AlertTriangle,
} from 'lucide-react';
import { formatPrice } from '../../utils/helpers';
import { toast } from '../../hooks/use-toast';
import { adminAPI } from '../../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAdminHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
});

const STATUS_LABELS = {
  pending_payment: 'Pendiente de Pago',
  paid: 'Pagado',
  accepted_by_seller: 'Aceptado por Vendedor',
  searching_driver: 'Buscando Repartidor',
  driver_assigned: 'Repartidor Asignado',
  in_transit: 'En Transito',
  shipped: 'Enviado',
  delivered_local: 'Entregado (Local)',
  delivered_national: 'Entregado (Nacional)',
  delivered: 'Entregado',
  cancelled: 'Cancelado',
};

const STATUS_COLORS = {
  pending_payment: 'bg-amber-500/20 text-amber-400',
  paid: 'bg-green-500/20 text-green-400',
  accepted_by_seller: 'bg-blue-500/20 text-blue-400',
  searching_driver: 'bg-purple-500/20 text-purple-400',
  driver_assigned: 'bg-cyan-500/20 text-cyan-400',
  in_transit: 'bg-indigo-500/20 text-indigo-400',
  shipped: 'bg-indigo-500/20 text-indigo-400',
  delivered_local: 'bg-emerald-500/20 text-emerald-400',
  delivered_national: 'bg-emerald-500/20 text-emerald-400',
  delivered: 'bg-emerald-500/20 text-emerald-400',
  cancelled: 'bg-red-500/20 text-red-400',
};

// ── Stepper Component ──
const STEPPER_LOCAL = [
  { key: 'paid', label: 'Pagado', icon: CreditCard },
  { key: 'searching_driver', label: 'Buscando Repartidor', icon: Search },
  { key: 'driver_assigned', label: 'Repartidor Asignado', icon: Truck },
  { key: 'in_transit', label: 'En Transito', icon: Truck },
  { key: 'delivered_local', label: 'Entregado', icon: CheckCircle },
];
const STEPPER_NACIONAL = [
  { key: 'paid', label: 'Pagado', icon: CreditCard },
  { key: 'accepted_by_seller', label: 'Preparando', icon: Package },
  { key: 'shipped', label: 'Enviado', icon: Truck },
  { key: 'delivered_national', label: 'Entregado', icon: CheckCircle },
];

const LogisticsStepper = ({ order }) => {
  const steps = order.delivery_type === 'local' ? STEPPER_LOCAL : STEPPER_NACIONAL;
  const currentIdx = steps.findIndex(s => s.key === order.status);

  return (
    <div className="flex items-center w-full gap-1" data-testid="logistics-stepper">
      {steps.map((step, i) => {
        const Icon = step.icon;
        const done = i <= currentIdx;
        const active = i === currentIdx;
        return (
          <React.Fragment key={step.key}>
            <div className="flex flex-col items-center min-w-0 flex-1">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
                done ? (active ? 'bg-orange-500 border-orange-500 scale-110 shadow-lg shadow-orange-500/30' : 'bg-green-600 border-green-600') : 'bg-gray-800 border-gray-600'
              }`}>
                {done && !active ? <CheckCircle className="w-5 h-5 text-white" /> : <Icon className={`w-5 h-5 ${done ? 'text-white' : 'text-gray-500'}`} />}
              </div>
              <p className={`text-[10px] mt-1.5 text-center leading-tight ${done ? 'text-white font-medium' : 'text-gray-600'}`}>
                {step.label}
              </p>
            </div>
            {i < steps.length - 1 && (
              <div className={`h-0.5 flex-1 mt-[-16px] rounded-full transition-all duration-500 ${i < currentIdx ? 'bg-green-500' : 'bg-gray-700'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ── Countdown Timer ──
const CountdownTimer = ({ startedAt }) => {
  const [remaining, setRemaining] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!startedAt) return;
    const calc = () => {
      const start = new Date(startedAt).getTime();
      const deadline = start + 5 * 60 * 1000;
      const diff = Math.max(0, deadline - Date.now());
      setRemaining(diff);
      if (diff <= 0 && intervalRef.current) clearInterval(intervalRef.current);
    };
    calc();
    intervalRef.current = setInterval(calc, 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [startedAt]);

  const mins = Math.floor(remaining / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);
  const expired = remaining <= 0;
  const urgent = remaining > 0 && remaining < 60000;

  return (
    <div className={`flex items-center gap-3 px-5 py-3 rounded-xl border ${
      expired ? 'bg-red-900/30 border-red-700' : urgent ? 'bg-orange-900/30 border-orange-600 animate-pulse' : 'bg-purple-900/20 border-purple-700'
    }`} data-testid="countdown-timer">
      <Timer className={`w-6 h-6 ${expired ? 'text-red-400' : urgent ? 'text-orange-400' : 'text-purple-400'}`} />
      <div>
        <p className={`text-2xl font-black font-mono ${expired ? 'text-red-400' : urgent ? 'text-orange-400' : 'text-purple-300'}`}>
          {expired ? '00:00' : `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`}
        </p>
        <p className="text-[10px] text-gray-500">
          {expired ? 'Tiempo agotado — reasignar o escalar' : 'Buscando repartidor disponible'}
        </p>
      </div>
    </div>
  );
};


const AdminOrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trackingInput, setTrackingInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [approvingPayment, setApprovingPayment] = useState(false);
  const [uploadingLabel, setUploadingLabel] = useState(false);

  const loadData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/orders/${orderId}/detail`, { headers: getAdminHeaders() });
      if (!res.ok) throw new Error('Not found');
      const d = await res.json();
      setData(d);
      setTrackingInput(d.order?.tracking_number || '');
    } catch {
      toast({ title: 'Error cargando detalle', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [orderId]);

  const handleSaveTracking = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/orders/${orderId}/tracking`, {
        method: 'PUT', headers: getAdminHeaders(),
        body: JSON.stringify({ tracking_number: trackingInput }),
      });
      if (!res.ok) throw new Error();
      toast({ title: 'Numero de guia guardado' });
      loadData();
    } catch {
      toast({ title: 'Error guardando guia', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleApprovePayment = async () => {
    if (!window.confirm('Aprobar este pago P2P? Se disparara el Motor Heisenberg.')) return;
    setApprovingPayment(true);
    try {
      await adminAPI.approvePayment(orderId);
      toast({ title: 'Pago aprobado', description: 'Heisenberg ha enrutado la orden' });
      loadData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'No se pudo aprobar', variant: 'destructive' });
    } finally { setApprovingPayment(false); }
  };

  const handleUploadLabel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLabel(true);
    try {
      await adminAPI.uploadShippingLabel(orderId, file);
      toast({ title: 'Guia PDF subida correctamente' });
      loadData();
    } catch (err) {
      toast({ title: 'Error subiendo PDF', description: err.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally { setUploadingLabel(false); }
  };

  const copyPin = (pin) => {
    navigator.clipboard.writeText(pin);
    toast({ title: `PIN ${pin} copiado` });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      </AdminLayout>
    );
  }

  if (!data?.order) {
    return (
      <AdminLayout>
        <div className="text-center py-20 text-gray-400">
          <Package className="w-16 h-16 mx-auto mb-4 opacity-40" />
          <p className="text-xl">Orden no encontrada</p>
          <Button variant="ghost" onClick={() => navigate('/admin/orders')} className="mt-4 text-orange-400">Volver</Button>
        </div>
      </AdminLayout>
    );
  }

  const { order, buyer, sellers, driver, timeline, algo_log, transactions } = data;
  const isLocal = order.delivery_type === 'local';
  const isNacional = order.delivery_type === 'nacional';
  const isPendingPayment = order.status === 'pending_payment';
  const isSearching = order.status === 'searching_driver';

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-order-detail">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/orders')} className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                Auditoria Cuantica
                <span className="font-mono text-orange-400 text-lg">#{(order.order_id || orderId).slice(0, 8).toUpperCase()}</span>
              </h1>
              <div className="flex items-center gap-3 mt-1">
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLORS[order.status] || 'bg-gray-600 text-gray-300'}`}>
                  {STATUS_LABELS[order.status] || order.status}
                </span>
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  {order.platform === 'app' ? <Smartphone className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                  {order.platform || 'web'}
                </span>
                <span className="text-xs text-gray-500">{new Date(order.created_at).toLocaleString('es-MX')}</span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-3xl font-black text-white">{formatPrice(order.total)}</p>
            <p className="text-xs text-gray-500">{order.payment_info?.method || '--'}</p>
          </div>
        </div>

        {/* ═══ LOGISTICS STEPPER ═══ */}
        {(isLocal || isNacional) && (
          <Card className="bg-gray-800 border-gray-700 p-6" data-testid="stepper-card">
            <LogisticsStepper order={order} />
            {/* Countdown for searching_driver */}
            {isSearching && order.driver_search_started_at && (
              <div className="mt-5">
                <CountdownTimer startedAt={order.driver_search_started_at} />
              </div>
            )}
          </Card>
        )}

        {/* P2P Payment Approval Banner */}
        {isPendingPayment && order.payment_info?.method === 'p2p' && (
          <Card className="bg-amber-900/20 border-amber-700/50 p-5" data-testid="p2p-approval-banner">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-400" />
                <div>
                  <p className="text-amber-300 font-semibold">Pago P2P pendiente de aprobacion</p>
                  <p className="text-amber-400/60 text-sm">Al aprobar se disparara el Motor Heisenberg automaticamente</p>
                </div>
              </div>
              <Button onClick={handleApprovePayment} disabled={approvingPayment}
                className="bg-green-600 hover:bg-green-700 text-white px-6" data-testid="approve-payment-btn">
                {approvingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-2" /> Aprobar Pago</>}
              </Button>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* ── Left: People + Logistics ── */}
          <div className="xl:col-span-2 space-y-4">
            {/* Buyer + Seller cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="bg-gray-800 border-gray-700 p-5">
                <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <User className="w-3.5 h-3.5" /> Comprador
                </h3>
                <p className="text-white font-semibold">{buyer?.name || order.buyer_name}</p>
                <p className="text-gray-400 text-sm">{buyer?.email || order.buyer_email}</p>
              </Card>
              <Card className="bg-gray-800 border-gray-700 p-5">
                <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Store className="w-3.5 h-3.5" /> Vendedor(es)
                </h3>
                {sellers?.length > 0 ? sellers.map((s, i) => (
                  <div key={i} className={i > 0 ? 'mt-2 pt-2 border-t border-gray-700' : ''}>
                    <p className="text-white font-semibold">{s.name}</p>
                    <p className="text-gray-400 text-sm">{s.email}</p>
                  </div>
                )) : <p className="text-gray-500 text-sm">No identificado</p>}
              </Card>
            </div>

            {/* Shipping */}
            <Card className="bg-gray-800 border-gray-700 p-5">
              <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5" /> Direccion de envio
              </h3>
              <p className="text-white">{order.shipping_address?.name}</p>
              <p className="text-gray-400 text-sm">{order.shipping_address?.address}</p>
              <p className="text-gray-400 text-sm">{order.shipping_address?.city}</p>
              <p className="text-gray-500 text-xs">{order.shipping_address?.phone}</p>
            </Card>

            {/* ══ QUANTUM STATE: Logistics Panel ══ */}
            <Card className="bg-gray-800 border-gray-700 p-5 border-l-4 border-l-purple-500" data-testid="logistics-panel">
              <h3 className="text-sm font-bold text-purple-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Zap className="w-4 h-4" /> Estado Cuantico — Logistica
              </h3>

              {algo_log && (
                <div className="bg-gray-900/50 rounded-lg p-3 mb-4 text-xs">
                  <p className="text-gray-500">Motor Heisenberg decidio:</p>
                  <p className="text-white font-mono mt-1">
                    Distancia: <span className="text-orange-400">{algo_log.distance_km} km</span>
                    {' '} → <span className={isLocal ? 'text-green-400' : 'text-blue-400'}>{algo_log.decision?.toUpperCase()}</span>
                  </p>
                </div>
              )}

              {isLocal && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                  <p className="text-green-400 font-semibold text-sm mb-3">Entrega Local — Triple Check Cuantico</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-900 rounded-lg p-4 text-center" data-testid="pickup-code">
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider">Codigo Recogida</p>
                      <p className="text-3xl font-black font-mono text-amber-400 mt-1">{order.pickup_code || '----'}</p>
                      <Button variant="ghost" size="sm" className="text-gray-500 text-xs mt-1" onClick={() => copyPin(order.pickup_code)}>
                        <Copy className="w-3 h-3 mr-1" /> Copiar
                      </Button>
                    </div>
                    <div className="bg-gray-900 rounded-lg p-4 text-center" data-testid="delivery-code">
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider">Codigo Entrega</p>
                      <p className="text-3xl font-black font-mono text-cyan-400 mt-1">{order.delivery_code || '----'}</p>
                      <Button variant="ghost" size="sm" className="text-gray-500 text-xs mt-1" onClick={() => copyPin(order.delivery_code)}>
                        <Copy className="w-3 h-3 mr-1" /> Copiar
                      </Button>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-green-500/20">
                    <p className="text-xs text-gray-500 flex items-center gap-1"><Truck className="w-3 h-3" /> Repartidor asignado</p>
                    {driver ? (
                      <p className="text-white font-medium mt-1">{driver.name} — <span className="text-gray-400">{driver.email}</span></p>
                    ) : (
                      <p className="text-amber-400 text-sm mt-1">Pendiente de asignar</p>
                    )}
                  </div>
                </div>
              )}

              {isNacional && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 space-y-4">
                  <p className="text-blue-400 font-semibold text-sm">Envio Nacional</p>

                  {/* Tracking number */}
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <label className="text-xs text-gray-500 block mb-1">Numero de guia</label>
                      <Input value={trackingInput} onChange={e => setTrackingInput(e.target.value)}
                        placeholder="Ej: 7701234567890" className="bg-gray-900 border-gray-600 text-white font-mono" data-testid="tracking-input" />
                    </div>
                    <Button onClick={handleSaveTracking} disabled={saving || !trackingInput.trim()}
                      className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-tracking-btn">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
                    </Button>
                  </div>
                  {order.tracking_number && (
                    <p className="text-green-400 text-xs flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3" /> Guia: <span className="font-mono">{order.tracking_number}</span>
                    </p>
                  )}

                  {/* PDF Upload */}
                  <div className="pt-3 border-t border-blue-500/20">
                    <p className="text-xs text-gray-400 mb-2 flex items-center gap-1"><FileText className="w-3 h-3" /> Guia PDF</p>
                    <div className="flex items-center gap-3">
                      <label className="cursor-pointer" data-testid="upload-label-btn">
                        <input type="file" accept=".pdf" onChange={handleUploadLabel} className="hidden" />
                        <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                          uploadingLabel ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-800 border-gray-600 text-white hover:bg-gray-700'
                        }`}>
                          {uploadingLabel ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                          Subir PDF
                        </span>
                      </label>
                      {order.shipping_label_url && (
                        <a href={order.shipping_label_url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-green-600 hover:bg-green-700 text-white transition-colors"
                          data-testid="download-label-btn">
                          <Download className="w-4 h-4" /> Descargar Guia
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!isLocal && !isNacional && !isPendingPayment && (
                <p className="text-gray-500 text-sm">Heisenberg aun no ha evaluado esta orden.</p>
              )}
            </Card>

            {/* Products */}
            <Card className="bg-gray-800 border-gray-700 p-5">
              <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Package className="w-3.5 h-3.5" /> Productos ({order.items?.length || 0})
              </h3>
              <div className="space-y-3">
                {order.items?.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    <div className="w-12 h-12 bg-gray-700 rounded overflow-hidden">
                      {item.product_image && (
                        <img src={item.product_image.startsWith('http') ? item.product_image : `${API_URL}/api${item.product_image}`}
                          alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-white">{item.product_title}{item.variant_label && <span className="text-gray-400"> · {item.variant_label}</span>}</p>
                      <p className="text-gray-500 text-xs">x{item.quantity} — {item.seller_name}</p>
                    </div>
                    <p className="text-white font-mono">{formatPrice(item.price * item.quantity)}</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* ── Right: Payment + Timeline ── */}
          <div className="space-y-4">
            <Card className="bg-gray-800 border-gray-700 p-5">
              <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <CreditCard className="w-3.5 h-3.5" /> Pago
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Metodo</span>
                  <span className="text-white capitalize">{order.payment_info?.method || '--'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Estado</span>
                  <span className={order.payment_info?.status === 'confirmed' ? 'text-green-400' : 'text-amber-400'}>
                    {order.payment_info?.status || '--'}
                  </span>
                </div>
                {order.payment_info?.mp_payment_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">MP ID</span>
                    <span className="text-white font-mono text-xs">{order.payment_info.mp_payment_id}</span>
                  </div>
                )}
              </div>
              {transactions?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-700">
                  <p className="text-xs text-gray-500 mb-2">Transacciones</p>
                  {transactions.map((t, i) => (
                    <div key={i} className="flex justify-between text-xs py-1">
                      <span className="text-gray-400">{t.type}</span>
                      <span className={t.amount < 0 ? 'text-red-400' : 'text-green-400'}>{formatPrice(Math.abs(t.amount))}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card className="bg-gray-800 border-gray-700 p-5 border-l-4 border-l-orange-500" data-testid="intelligence-timeline">
              <h3 className="text-sm font-bold text-orange-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Eye className="w-4 h-4" /> Linea de Tiempo
              </h3>
              {timeline.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-6">Sin eventos de sesion registrados</p>
              ) : (
                <div className="relative">
                  <div className="absolute left-3 top-0 bottom-0 w-px bg-gray-700" />
                  <div className="space-y-4">
                    {timeline.slice(-30).map((event, i) => (
                      <div key={i} className="relative pl-8">
                        <div className="absolute left-1.5 top-1 w-3 h-3 rounded-full bg-gray-700 border-2 border-orange-500" />
                        <div>
                          <p className="text-white text-xs font-medium">{event.event_type || event.action || 'event'}</p>
                          {event.url && <p className="text-gray-500 text-[10px] truncate max-w-[200px]">{event.url}</p>}
                          <p className="text-gray-600 text-[10px]">
                            {event.timestamp ? new Date(event.timestamp).toLocaleString('es-MX') : '--'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminOrderDetail;
